import type { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from "next";
import { useRouter } from "next/router";

import { ArchiveSeo, indexCopy } from "@/components/blog/ArchiveSeo";
import { BlogArchive } from "@/components/blog/BlogArchive";
import Footer from "@/components/shared/Footer";
import { blogPagePath } from "@/lib/blog/paths";
import { useClientFallback } from "@/lib/blog/useClientFallback";
import { getPostPage, type PostPage } from "@/lib/ghost";
import type { BreadcrumbItem } from "@/lib/seo/schema";

const parsePageNumber = (value: unknown) => {
  if (typeof value !== "string" || !/^[1-9][0-9]*$/.test(value)) return 0;
  return Number(value);
};

export const getStaticPaths = (async () => ({
  paths: [],
  fallback: "blocking",
})) satisfies GetStaticPaths;

export const getStaticProps = (async (context) => {
  const pageNumber = parsePageNumber(context.params?.n);
  if (!pageNumber) return { notFound: true as const };
  if (pageNumber === 1) return { redirect: { destination: "/blog", permanent: true } };

  const page = await getPostPage(pageNumber);
  if (page && (pageNumber > page.pages || !page.posts.length)) return { notFound: true as const };

  return {
    props: { page, pageNumber },
    revalidate: 3600,
  };
}) satisfies GetStaticProps<{ page: PostPage | null; pageNumber: number }>;

export default function BlogPaginatedPage({
  page,
  pageNumber,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const router = useRouter();
  const current = parsePageNumber(router.query.n) || pageNumber || 0;
  const { data, loading } = useClientFallback(page, current ? `blog:${current}` : "", () =>
    getPostPage(current),
  );

  const copy = indexCopy(current, data?.pages ?? current);
  const path = blogPagePath(current);
  const breadcrumbs: BreadcrumbItem[] = [
    { name: "Home", href: "/" },
    { name: "Blog", href: "/blog" },
    { name: `Page ${current}`, href: path },
  ];

  return (
    <>
      <ArchiveSeo copy={copy} path={path} posts={data?.posts ?? []} breadcrumbs={breadcrumbs} />
      <BlogArchive
        page={data}
        loading={loading}
        heading={copy.heading}
        intro={copy.intro}
        breadcrumbs={breadcrumbs}
        pathFor={blogPagePath}
      />
      <Footer />
    </>
  );
}
